export default class Focus {
  /**
   * Sets focus to the given element after an optional delay.
   *
   * @param {HTMLElement} el - The element to focus.
   * @param {number} [timeout=1] - Delay in milliseconds before setting focus.
   */
  static setFocusToElement(el, timeout = 1) {
    if (!el) {
      return;
    }

    setTimeout(() => {
      el.focus();
    }, timeout);
  }

  /**
   * Finds the next focusable element in the document, optionally skipping elements within
   * a given container or restricting focus to a parent element.
   *
   * Useful for handling custom keyboard navigation (e.g. in modals or menus).
   *
   * @param {HTMLElement|null} [elementWhichChildrenAreExcluded=null] - If set, all focusable children inside this element will be excluded from navigation.
   * @param {boolean} [forward=true] - Direction of navigation: `true` for forward (Tab), `false` for backward (Shift+Tab).
   * @param {HTMLElement|null} [activeElementParam=null] - Optional override for the currently active element (defaults to `document.activeElement`).
   * @param {HTMLElement|null} [allowedParentElement=null] - If set, only elements within this container are considered focusable.
   * @returns {HTMLElement|null} The next focusable element or `null` if none is found.
   */
  static getNextFocusableElement(elementWhichChildrenAreExcluded = null, forward = true, activeElementParam = null, allowedParentElement = null) {
    const activeElement = activeElementParam || document.activeElement;

    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusableElements = Array.from(document.querySelectorAll(selector))
      .filter(node => node.offsetParent !== null);

    let idx = focusableElements.indexOf(activeElement);
    const step = forward ? 1 : -1;
    let next = null;

    idx += step;
    while (idx >= 0 && idx < focusableElements.length) {
      const candidate = focusableElements[idx];

      // Skip if the current slot is empty (null or undefined)
      if (!candidate) {
        idx += step;
        continue;
      }

      // Skip if the candidate sits inside a container whose children are excluded
      if (elementWhichChildrenAreExcluded?.contains(candidate)) {
        idx += step;
        continue;
      }

      // Skip if any ancestor of the candidate has the [inert] attribute
      if (candidate.closest('[inert]')) {
        idx += step;
        continue;
      }

      // If an "allowed" selector is set, skip everything outside of it
      if (allowedParentElement && !allowedParentElement.contains(candidate)) {
        idx += step; continue;
      }

      // Valid focus target found
      next = candidate;
      break;
    }

    // if no next element left -> focus on document body
    if (!next) {
      document.body.tabIndex = -1;
      Focus.setFocusToElement(document.body);
      setTimeout(() => document.body.removeAttribute('tabindex'), 10);
      // leave next on null to prevent other function reactions
    }

    return next;
  }
}
